import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import {
  MrfiktivDocumentRuleHistoryEntryViewModelGen,
  MrfiktivDocumentRuleViewModelGen,
  MrfiktivMonetaryImpactConfigViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import documentRuleService from "@/services/mrfiktiv/services/documentRuleService";
import { DocumentRuleDataAccessLayer } from "@/store/modules/access-layers/document-rule.access-layer";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";

@IsFilterable
class DocumentRuleBase implements IEntity<IDocumentRule>, MrfiktivDocumentRuleViewModelGen {
  id: string;
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.anonymization.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  userId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "components.DocumentRuleTable.headerRule"
  })
  rule: string;
  tags: string[];

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "description"
  })
  description?: string | undefined;
  active: boolean;
  severity?: "info" | "warning" | "error" | undefined;
  source?: string | undefined;
  monetaryImpactConfig?: MrfiktivMonetaryImpactConfigViewModelGen | null;
  adminRuleId?: string;

  history: MrfiktivDocumentRuleHistoryEntryViewModelGen[];

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  loading = false;

  constructor(data: Partial<MrfiktivDocumentRuleViewModelGen>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.rule = data.rule ?? "";
    this.tags = data.tags ?? [];
    this.description = data.description ?? "";
    this.active = data.active ?? false;
    this.severity = data.severity;
    this.source = data.source ?? "";
    this.monetaryImpactConfig = data.monetaryImpactConfig;
    this.adminRuleId = data.adminRuleId;
    this.history = data.history ?? [];

    this.timestamp = new Timestamp(data.timestamp);
  }

  map(data: MrfiktivDocumentRuleViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.userId = data.userId;
    this.rule = data.rule;
    this.tags = data.tags;
    this.description = data.description;
    this.active = data.active;
    this.severity = data.severity;
    this.source = data.source;
    this.monetaryImpactConfig = data.monetaryImpactConfig;
    this.adminRuleId = data.adminRuleId;
    this.history = data.history ?? [];
    this.timestamp = new Timestamp(data.timestamp);
  }

  async create(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    this.map(
      await documentRuleService.create(this.partnerId, {
        rule: this.rule,
        tags: this.tags,
        description: this.description,
        active: this.active,
        severity: this.severity,
        source: this.source,
        monetaryImpactConfig: this.monetaryImpactConfig
      })
    );

    DocumentRuleDataAccessLayer.set(this);
    return this;
  }

  async fetch(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    if (!this.id) {
      throw new Error("Id not set");
    }
    this.map(await documentRuleService.getOne(this.partnerId, this.id));
    DocumentRuleDataAccessLayer.set(this);
    return this;
  }

  async delete() {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    if (!this.id) {
      throw new Error("Id not set");
    }
    this.map(await documentRuleService.delete(this.partnerId, this.id));
    DocumentRuleDataAccessLayer.delete(this);
  }

  async update(): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    if (!this.id) {
      throw new Error("Id not set");
    }
    this.map(
      await documentRuleService.update(this.partnerId, this.id, {
        rule: this.rule,
        tags: this.tags,
        description: this.description,
        active: this.active,
        severity: this.severity,
        source: this.source,
        monetaryImpactConfig: this.monetaryImpactConfig
      })
    );

    DocumentRuleDataAccessLayer.set(this);
    return this;
  }

  async updateAdminRuleLink(adminRuleId: string | null): Promise<this> {
    if (!this.partnerId) {
      throw new Error("Partnerid not set");
    }
    if (!this.id) {
      throw new Error("Id not set");
    }
    this.map(await documentRuleService.linkAdminRule(this.partnerId, this.id, adminRuleId));
    DocumentRuleDataAccessLayer.set(this);
    return this;
  }
}

type IDocumentRule = DocumentRuleBase;
const DocumentRule = Filter.createForClass(DocumentRuleBase);

export { DocumentRule, IDocumentRule };
